import { FC, useMemo, useState } from 'react';

import {
  CustomerAnalyticsItemType,
  CustomerAnalyticsResponse,
  GiftItemProfitRow,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import { TD_CLASS, TH_CLASS } from '../../components/DashboardShared';
import { fmt } from './analysisUtils';

const fmtShare = (v: number): string => `${(v * 100).toFixed(1)}%`;
const fmtMargin = (v: number | null): string =>
  v === null ? '-' : `${(v * 100).toFixed(1)}%`;

/** 수량이 절반 이하로 꺾이면 저항선으로 본다 (직전 밴드 수량 5개 이상일 때만 판정) */
const CLIFF_RATIO = 0.5;
const CLIFF_MIN_PREV_QTY = 5;

interface BandRow {
  label: string;
  min: number;
  max: number | null;
  amountShare: number; // 0–1
  share: number; // 수량 비중 0–1
  quantity: number;
  netAmount: number;
  /** ΣtotalProfit / ΣtotalRevenue (profit-analysis-detail). null = 마진 데이터 없음 */
  margin: number | null;
  gap: number | null; // 목표마진 − 현재마진 (0–1, 음수면 0)
  impact: number | null; // amountShare × gap → 전체 수익률 개선 잠재치 (0–1)
  itemCount: number; // 밴드에 매핑된 상품 수 (profit축)
}

/**
 * "어느 가격대의 수익률 개선이 실질 임팩트가 큰지" — 스펙 문서 하단 절차의 프론트 구현.
 * 임팩트 = 밴드 매출 비중(amountShare) × 개선 여지(목표마진 − 현재마진).
 *
 * - 매출 비중: customer-analytics unitPriceDistribution (라인축)
 * - 밴드 마진율: profit-analysis-detail giftItems를 같은 밴드로 묶어 ΣtotalProfit/ΣtotalRevenue
 *   (낙전 포함 실현 마진 — 판매가축이라 라인축 금액과 원 단위까진 안 맞지만 우선순위 판단에는 충분)
 * - 밴드 매핑: byItem.avgUnitPrice(실청구 단가) 기준, byItem에 없는 상품은 priceRetail 폴백
 * - 목표마진 기본값: 밴드 중 최고 마진율 (베스트 밴드를 벤치마크로) — 입력으로 덮어쓰기 가능
 */
export const PriceBandImpactSection: FC<{
  analytics: CustomerAnalyticsResponse;
  /** itemType 필터가 걸려 있으면 호출부가 같은 유형으로 걸러서 준다. null = 로드 실패 */
  profitRows: GiftItemProfitRow[] | null;
  /** 현재 보기 유형 — 구매자(상주)가 동일하므로 쿠폰형 수요는 배송형 구성의 근거가 된다 */
  itemType: 'all' | CustomerAnalyticsItemType;
}> = ({ analytics, profitRows, itemType }) => {
  const [targetInput, setTargetInput] = useState(''); // '' = 자동(최고 밴드)

  const buckets = analytics.buyers.unitPriceDistribution;

  const computed = useMemo(() => {
    const bandOf = (price: number) =>
      buckets.findIndex(
        (b) => price >= b.min && (b.max === null || price < b.max),
      );

    // 상품 → 밴드: 실청구 평균단가(byItem) 우선
    const itemBand = new Map<string, number>();
    for (const item of analytics.byItem) {
      itemBand.set(item.giftItemId, bandOf(item.avgUnitPrice));
    }

    const grouped = buckets.map(() => ({
      profit: 0,
      revenue: 0,
      items: 0,
    }));
    for (const row of profitRows ?? []) {
      const idx = itemBand.get(row.giftItemId) ?? bandOf(row.priceRetail);
      const g = grouped[idx]; // idx < 0 이면 undefined
      if (!g) continue;
      g.profit += row.totalProfit;
      g.revenue += row.totalRevenue;
      g.items++;
    }

    const margins = grouped.map((g) =>
      g.revenue > 0 ? g.profit / g.revenue : null,
    );
    const validMargins = margins.filter((m): m is number => m !== null);
    const autoTarget = validMargins.length > 0 ? Math.max(...validMargins) : null;

    const parsed = parseFloat(targetInput);
    const target =
      targetInput !== '' && !isNaN(parsed) ? parsed / 100 : autoTarget;

    const bands: BandRow[] = buckets.map((b, i) => {
      const margin = margins[i] ?? null;
      const gap =
        margin !== null && target !== null ? Math.max(0, target - margin) : null;
      return {
        label: b.label,
        min: b.min,
        max: b.max,
        amountShare: b.amountShare,
        share: b.share,
        quantity: b.quantity,
        netAmount: b.netAmount,
        margin,
        gap,
        impact: gap !== null ? b.amountShare * gap : null,
        itemCount: grouped[i]?.items ?? 0,
      };
    });

    // 마진 데이터가 아예 없으면 매출 비중으로 정렬 (임팩트의 가중치 축은 그대로 유효).
    // 마진 데이터가 있는데 특정 밴드만 매핑이 안 됐으면 그 밴드는 0으로 취급
    // (impact ?? amountShare 폴백은 스케일이 달라 순위를 왜곡한다) — 동률은 매출 비중순.
    const hasProfitData = (profitRows?.length ?? 0) > 0;
    const rankKey = (b: BandRow) =>
      hasProfitData ? (b.impact ?? 0) : b.amountShare;
    const ranked = [...bands]
      .filter((b) => b.amountShare > 0)
      .sort(
        (a, b) => rankKey(b) - rankKey(a) || b.amountShare - a.amountShare,
      );
    const winner = ranked[0] ?? null;

    // 가격 저항선: 인접 밴드 대비 수량이 가장 크게 꺾이는 경계
    let cliffIdx = -1;
    let worstRatio = 1;
    for (let i = 1; i < buckets.length; i++) {
      const prev = buckets[i - 1]?.quantity ?? 0;
      const cur = buckets[i]?.quantity ?? 0;
      if (prev < CLIFF_MIN_PREV_QTY) continue;
      const ratio = cur / prev;
      if (ratio < worstRatio) {
        worstRatio = ratio;
        cliffIdx = i;
      }
    }
    const hasCliff = cliffIdx > 0 && worstRatio <= CLIFF_RATIO;
    const cliffFrom = hasCliff ? (buckets[cliffIdx - 1]?.label ?? '') : '';
    const cliffTo = hasCliff ? (buckets[cliffIdx]?.label ?? '') : '';
    // "얼마짜리까지 보낼 수 있나" — 저항선 아래 밴드들의 수량 누적 비중
    const demandBelowCliff = hasCliff
      ? buckets.slice(0, cliffIdx).reduce((s, b) => s + b.share, 0)
      : null;

    // 주력 밴드의 볼륨 드라이버 (도매가 협상 대상)
    const winnerIdx = winner ? bands.indexOf(winner) : -1;
    const drivers = winner
      ? analytics.byItem
          .filter((i) => itemBand.get(i.giftItemId) === winnerIdx)
          .sort((a, b) => b.netQuantity - a.netQuantity)
          .slice(0, 3)
      : [];
    // 옵션 부착 여지: 실청구단가 − 카탈로그가 (저항선까지 단가를 올리는 안전 경로)
    const optionDelta =
      drivers.length > 0
        ? Math.round(
            drivers.reduce((s, i) => s + (i.avgUnitPrice - i.priceRetail), 0) /
              drivers.length,
          )
        : 0;

    return {
      bands,
      ranked,
      winner,
      winnerIdx,
      target,
      autoTarget,
      hasCliff,
      cliffIdx,
      cliffFrom,
      cliffTo,
      worstRatio,
      demandBelowCliff,
      drivers,
      optionDelta,
    };
  }, [analytics, profitRows, buckets, targetInput]);

  const {
    ranked,
    winner,
    winnerIdx,
    target,
    autoTarget,
    hasCliff,
    cliffIdx,
    cliffFrom,
    cliffTo,
    worstRatio,
    demandBelowCliff,
    drivers,
    optionDelta,
  } = computed;

  if (!winner) return null;

  const hasMargin = winner.impact !== null;

  // 구성 가이드용: 1인당 수량(≈ 몇 명에게 보내는지) × 주력 밴드 평균단가 × 목표마진
  const qtyMedian = analytics.buyers.quantityPerBuyer.median;
  const qtyAvg = analytics.buyers.quantityPerBuyer.avg;
  const winnerAvgPrice =
    winner.quantity > 0 ? Math.round(winner.netAmount / winner.quantity) : 0;
  const expectedPerBuyer =
    target !== null && winnerAvgPrice > 0
      ? Math.round(winnerAvgPrice * qtyMedian * target)
      : null;

  // 저항선 대비 주력 밴드 위치에 따른 개선 경로 조언
  const advice = (() => {
    if (!hasCliff) {
      return '뚜렷한 수량 절벽이 없어 가격대 전반에서 원가(도매가)·수수료 개선이 우선입니다.';
    }
    if (winnerIdx === cliffIdx - 1) {
      return (
        `주력 밴드가 가격 저항선 바로 아래입니다 — 정가 인상 대신 옵션 추가금으로 저항선까지 단가를 올리는 게 안전한 경로` +
        (optionDelta > 0
          ? ` (현재 옵션 부착 평균 +${fmt(optionDelta)}원)`
          : ' (현재 옵션 부착 거의 없음 → 여지 큼)')
      );
    }
    if (winnerIdx < cliffIdx) {
      return '주력 밴드가 저항선 아래라 가격 인상 여지가 제한적입니다 — 같은 판매가에서 도매가·수수료 개선이 답입니다.';
    }
    return '주력 밴드가 저항선 위라 수요가 얇습니다 — 볼륨 확대보다 건당 마진(도매가 협상) 중심으로.';
  })();

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-800">
          어느 가격대에 주력할까 — 수익 개선 임팩트
        </h3>
        <label className="flex items-center gap-1.5 text-xs text-gray-500">
          목표 마진율
          <input
            type="number"
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            placeholder={
              autoTarget !== null ? (autoTarget * 100).toFixed(1) : '-'
            }
            className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-right text-xs"
          />
          %
          <span className="text-gray-400">(비우면 최고 밴드 기준)</span>
        </label>
      </div>

      {/* 결론 */}
      <div className="border-b bg-indigo-50 px-4 py-3">
        <p className="text-sm font-semibold text-indigo-900">
          💡 {winner.label} 밴드 1순위 — 매출의 {fmtShare(winner.amountShare)}
          {hasMargin && target !== null && (
            <>
              , 현재 마진 {fmtMargin(winner.margin)} → 목표{' '}
              {fmtMargin(target)} 달성 시 전체 수익률{' '}
              <span className="text-indigo-700">
                +{((winner.impact ?? 0) * 100).toFixed(2)}%p
              </span>
            </>
          )}
          {!hasMargin && ' (마진 데이터 없음 — 매출 비중 기준 정렬)'}
        </p>
        <ul className="mt-2 space-y-1 text-xs text-indigo-900/80">
          <li>
            • <span className="font-medium">몇 명에게:</span> 구매자는 보통{' '}
            {qtyMedian.toFixed(1)}명(중앙값)에게 보냅니다 (평균{' '}
            {qtyAvg.toFixed(1)}명)
          </li>
          <li>
            • <span className="font-medium">얼마짜리까지:</span>{' '}
            {hasCliff
              ? `${cliffFrom} → ${cliffTo} 경계에서 수량 ${Math.round((1 - worstRatio) * 100)}% 급감 — 수요의 ${demandBelowCliff !== null ? fmtShare(demandBelowCliff) : '-'}가 그 아래`
              : '뚜렷한 가격 저항선 없음'}{' '}
            — {advice}
          </li>
          {expectedPerBuyer !== null && (
            <li>
              • <span className="font-medium">구성 가이드:</span> {winner.label}{' '}
              밴드(평균단가 {fmt(winnerAvgPrice)}원)를 마진율{' '}
              {fmtMargin(target)}로 구성하면 구매자 1명당 기대 마진 ≈{' '}
              <span className="font-semibold">
                {fmt(expectedPerBuyer)}원
              </span>{' '}
              ({fmt(winnerAvgPrice)}원 × {qtyMedian.toFixed(1)}개 ×{' '}
              {fmtMargin(target)})
            </li>
          )}
          <li>
            • 이 밴드 마진 1%p 개선 = 전체 수익률 +
            {(winner.amountShare * 1).toFixed(2)}%p (매출 비중이 가중치)
          </li>
          {drivers.length > 0 && (
            <li>
              • 도매가 협상 1순위(볼륨 드라이버):{' '}
              {drivers
                .map((d) => `${d.name} (${fmt(d.netQuantity)}개)`)
                .join(' · ')}
            </li>
          )}
          {itemType !== 'Delivery' && (
            <li>
              • <span className="font-medium">배송형 적용:</span> 구매자는
              답례하는 상주로 동일하므로 위{' '}
              {itemType === 'Coupon' ? '쿠폰형' : '전체'} 수요(몇 명에게 ·
              얼마짜리)는 배송형 상품군 구성에 그대로 쓸 수 있는 근거입니다.
              단, 마진율 목표는 배송형 원가 구조(도매가+택배비)로 다시
              잡으세요.
            </li>
          )}
        </ul>
      </div>

      {/* 밴드별 표 (임팩트순) */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH_CLASS}>순위</th>
              <th className={TH_CLASS}>가격대</th>
              <th className={`${TH_CLASS} text-right`}>매출 비중</th>
              <th className={`${TH_CLASS} text-right`}>수량 비중</th>
              <th className={`${TH_CLASS} text-right`}>현재 마진율</th>
              <th className={`${TH_CLASS} text-right`}>개선 여지</th>
              <th className={`${TH_CLASS} text-right`}>전체 임팩트</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {ranked.map((band, i) => (
              <tr
                key={band.label}
                className={`hover:bg-gray-50 ${i === 0 ? 'bg-indigo-50/50' : ''}`}
              >
                <td className={`${TD_CLASS} ${i === 0 ? 'font-bold text-indigo-700' : 'text-gray-400'}`}>
                  {i + 1}
                </td>
                <td className={`${TD_CLASS} font-medium`}>{band.label}</td>
                <td className={`${TD_CLASS} text-right`}>
                  {fmtShare(band.amountShare)}
                  <span className="block text-xs text-gray-400">
                    {fmt(band.netAmount)}원
                  </span>
                </td>
                <td className={`${TD_CLASS} text-right text-gray-500`}>
                  {fmtShare(band.share)}
                </td>
                <td className={`${TD_CLASS} text-right`}>
                  {fmtMargin(band.margin)}
                  {band.itemCount > 0 && (
                    <span className="block text-xs text-gray-400">
                      {band.itemCount}종
                    </span>
                  )}
                </td>
                <td className={`${TD_CLASS} text-right`}>
                  {band.gap !== null ? `${(band.gap * 100).toFixed(1)}%p` : '-'}
                </td>
                <td
                  className={`${TD_CLASS} text-right font-medium ${i === 0 ? 'text-indigo-700' : ''}`}
                >
                  {band.impact !== null
                    ? `+${(band.impact * 100).toFixed(2)}%p`
                    : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="border-t px-4 py-2 text-xs text-gray-400">
        임팩트 = 매출 비중 × (목표마진 − 현재마진). 마진율은 수익성
        분석(profit-analysis-detail)의 실현 마진이고, 밴드 매핑은 실청구
        평균단가 기준(없으면 카탈로그가)입니다. 매출 비중은 라인축, 마진은
        판매가축이라 금액이 원 단위까지 일치하지는 않습니다.
        {profitRows === null &&
          ' ⚠️ 마진 데이터를 불러오지 못해 매출 비중 기준으로만 정렬했습니다.'}
      </p>
    </div>
  );
};
